"use client";

import { usePathname, useRouter } from "next/navigation";
import { startTransition, useCallback, useEffect, useMemo } from "react";
import type { ProductGetResult } from "@/dao/product";
import { cn } from "@/lib/utils";

type VariantValueInGroup = { id: string; value: string; position: number };
type VariantTypeInGroup = { id: string; label: string };
type Group = {
	variantType: VariantTypeInGroup;
	variantValues: VariantValueInGroup[];
};

export const ProductVariantSelector = ({
	variants,
	optimisticSearchParams,
	setOptimisticSearchParams,
}: {
	variants: ProductGetResult["variants"];
	optimisticSearchParams: Record<string, string>;
	setOptimisticSearchParams: (action: Record<string, string>) => void;
}) => {
	const router = useRouter();
	const pathname = usePathname();

	const groupedByVariantType = useMemo(
		() =>
			Object.values(
				variants
					.flatMap((item) => item.combinations)
					.reduce<Record<string, Group>>((acc, combination) => {
						const { label } = combination.variantValue.variantType;
						const { id, value, position } = combination.variantValue;

						if (!acc[label]) {
							acc[label] = {
								variantType: { id: combination.variantValue.variantType.id, label },
								variantValues: [],
							};
						}

						// Avoid adding duplicate variantValues
						if (!acc[label].variantValues.some((v) => v.id === id)) {
							acc[label].variantValues.push({ id, value, position: position || 1 });
						}

						return acc;
					}, {}),
			).map((v) => ({
				...v,
				variantValues: v.variantValues.toSorted((v1, v2) => v1.position - v2.position),
			})),
		[variants],
	);

	const selectVariant = useCallback(
		({
			group,
			variantValue,
			replace,
		}: {
			group: Group;
			variantValue: VariantValueInGroup;
			replace: boolean;
		}) => {
			const newParams = {
				...optimisticSearchParams,
				[group.variantType.label]: variantValue.value,
			};

			startTransition(() => setOptimisticSearchParams(newParams));

			const path = pathname + "?" + new URLSearchParams(newParams).toString();
			if (replace) {
				router.replace(path, { scroll: false });
			} else {
				router.push(path, { scroll: false });
			}
		},
		[optimisticSearchParams, pathname, setOptimisticSearchParams],
	);

	useEffect(() => {
		// automatically check variant if there's only one

		const groupsToAutoSelect = groupedByVariantType.filter((group) => group.variantValues.length <= 1);
		if (groupsToAutoSelect.length === 0) {
			return;
		}
		groupsToAutoSelect.map((group) => {
			const variantValue = group?.variantValues[0];
			const isChecked =
				variantValue &&
				group?.variantType.label &&
				optimisticSearchParams[group.variantType.label] === variantValue.value;
			if (isChecked) {
				return;
			}

			if (group && variantValue) {
				selectVariant({ group, variantValue, replace: true });
				console.log("select");
			}
		});
	}, [groupedByVariantType, selectVariant, optimisticSearchParams]);

	return (
		<ul role="list" className="grid gap-2" aria-labelledby="variant-label">
			{groupedByVariantType.map((group) => {
				return (
					<li key={group.variantType.id} className="grid gap-1">
						<h4 className="font-semibold text-sm">{group.variantType.label}</h4>
						<ul className="flex flex-row gap-2 flex-wrap">
							{group.variantValues.map((variantValue) => {
								const isChecked = optimisticSearchParams[group.variantType.label] === variantValue.value;
								const shouldBeDisabled = shouldDisableButton(
									variants,
									optimisticSearchParams,
									group.variantType.label,
									variantValue.value,
								);

								return (
									<li key={variantValue.id}>
										<label
											className={cn(
												"flex cursor-pointer items-center justify-center gap-2 rounded-md border px-8 py-2 transition-colors hover:bg-(--theme-primary-background-color)/80",
												isChecked && "border-black bg-(--theme-primary-background-color)/60 font-medium",
												"has-disabled:text-neutral-400",
												"has-focus-visible:ring-3",
											)}
										>
											{variantValue.value}
											<input
												type="radio"
												name={group.variantType.label}
												value={variantValue.value}
												checked={isChecked}
												disabled={shouldBeDisabled}
												className="sr-only"
												onChange={() => {
													selectVariant({ group, variantValue, replace: false });
												}}
											/>
										</label>
									</li>
								);
							})}
						</ul>
					</li>
				);
			})}
		</ul>
	);
};

// Generated with GPT-4o and the following prompt:
// - I have a button for each variant type and variant value (e.g. size S, M, L, color Red, Blue, Green)
// - When that button is clicked, the variantValue for this variantType is selected
// - product.variants is an array that consists of allowed combinations of variantValues
// - determine whether a button should be disabled if a combination doesn't exist on product.variants
// - it should only disable this current button but leave other buttons enabled, i.e. return different answers for different buttons
// - for example, if allowed combinations for Size and Color are: [Red:S, Red:M, Red:L, Blue:S, Blue:L, Green:S, Green:M, Green:L]
//   then selecting Blue should disable M and selecting M should disable Blue but all other buttons should remain enabled.
function shouldDisableButton(
	variants: ProductGetResult["variants"],
	searchParams: Record<string, string | undefined>,
	variantTypeLabel: string,
	variantValueValue: string,
) {
	const simulatedSelection = { ...searchParams, [variantTypeLabel]: variantValueValue };

	const isValid = variants.some(
		(variant) =>
			variant.combinations.every((combination) => {
				const label = combination.variantValue.variantType.label;
				const val = combination.variantValue.value;
				return simulatedSelection[label] === undefined || simulatedSelection[label] === val;
			}) &&
			(variant.stock === null || variant.stock > 0),
	);

	return !isValid;
}
